// src/views/adminView/contractsView/compliance/complianceUtils.js
// ─────────────────────────────────────────────────────────────────────────────
// Lógica PURA del sistema de cumplimiento documental (sin React, sin i18n
// directo). Las empresas son los contracts reales del backend; el estado
// documental (salvo el contrato marco, que sale del contract real) es
// front-only y vive en localStorage (ver useComplianceStore.js).
//
// i18n: las funciones que producen texto visible reciben `t` como parámetro
// (con un fallback en español que interpola igual que i18next) para que todo
// lo renderizado pase por t() en el componente que lo pinta.
// ─────────────────────────────────────────────────────────────────────────────

export const DOC_EVAL = 'dt_cli_eval_crediticia';
export const DOC_CONTRATO = 'dt_cli_contrato_marco';
export const CRITICOS = [DOC_EVAL, DOC_CONTRATO];

export const PLANTILLA_NOMBRE = 'Clientes con cuenta corriente · Chile';

// Plantilla de reglas documentales (preset Chile). 5 obligatorios → denominador
// de todas las barras X/Y; el pagaré es opcional.
export const PLANTILLA_REGLAS = [
  {
    docTipoId: DOC_EVAL,
    nombre: 'Evaluación crediticia',
    obligatorio: true,
    vigenciaMeses: 12,
    bloqueoAlVencer: true,
    recordatoriosDias: [60, 30, 15, 7],
  },
  {
    docTipoId: DOC_CONTRATO,
    nombre: 'Contrato marco',
    obligatorio: true,
    vigenciaMeses: 12,
    bloqueoAlVencer: true,
    recordatoriosDias: [30, 15, 7],
    requiereFirma: true,
  },
  {
    docTipoId: 'dt_cli_constitucion',
    nombre: 'Constitución de sociedad',
    obligatorio: true,
    bloqueoAlVencer: false,
    recordatoriosDias: [30, 15, 7],
  },
  {
    docTipoId: 'dt_cli_inicio_actividades',
    nombre: 'Inicio de actividades (SII)',
    obligatorio: true,
    bloqueoAlVencer: false,
    recordatoriosDias: [30, 15, 7],
  },
  {
    docTipoId: 'dt_cli_dicom',
    nombre: 'Informe DICOM',
    obligatorio: true,
    vigenciaMeses: 3,
    bloqueoAlVencer: false,
    recordatoriosDias: [15, 7],
  },
  {
    docTipoId: 'dt_ots_pagare',
    nombre: 'Pagaré en garantía',
    obligatorio: false,
    bloqueoAlVencer: false,
    recordatoriosDias: [30, 15, 7],
    requiereFirma: true,
  },
];

// Fallback de i18n: interpola {{var}} sobre el literal en español, igual que
// haría i18next sin catálogo. Permite llamar estas funciones sin t().
const plain = (s, vars) =>
  vars ? s.replace(/\{\{(\w+)\}\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m)) : s;

/** Días (redondeados) desde `hoy` hasta la fecha ISO. Negativo = ya pasó. */
export function diasHasta(iso, hoy = new Date()) {
  return Math.round((new Date(iso) - hoy) / 86400000);
}

/**
 * Semáforo de un documento según su vencimiento. Sin fecha → 'vigente'.
 * Umbral de 30 días INCLUSIVE; el día 0 (vence hoy) es 'por_vencer'.
 */
export function estadoDocPara(venceAt, hoy = new Date()) {
  if (!venceAt) return 'vigente';
  const dias = diasHasta(venceAt, hoy);
  if (dias < 0) return 'vencido';
  if (dias <= 30) return 'por_vencer';
  return 'vigente';
}

/** Fecha ISO de vencimiento según la regla, contando desde `desde` (hoy). */
export function venceSegunRegla(regla, desde = new Date()) {
  if (!regla?.vigenciaMeses) return undefined;
  const d = new Date(desde);
  d.setMonth(d.getMonth() + regla.vigenciaMeses);
  return d.toISOString();
}

/**
 * Descripción legible de la regla, p.ej.:
 * 'Vigencia 1 año · bloquea al vencer · avisos 60/30/15/7 d'.
 */
export function textoRegla(regla, t = plain) {
  const partes = [];
  if (regla.vigenciaMeses) {
    if (regla.vigenciaMeses % 12 === 0) {
      const n = regla.vigenciaMeses / 12;
      partes.push(n > 1 ? t('Vigencia {{n}} años', { n }) : t('Vigencia {{n}} año', { n }));
    } else {
      partes.push(t('Vigencia {{n}} meses', { n: regla.vigenciaMeses }));
    }
  } else {
    partes.push(t('No vence'));
  }
  if (regla.requiereFirma) partes.push(t('requiere firma'));
  if (regla.bloqueoAlVencer) partes.push(t('bloquea al vencer'));
  if (regla.recordatoriosDias?.length) {
    partes.push(t('avisos {{dias}} d', { dias: regla.recordatoriosDias.join('/') }));
  }
  return partes.join(' · ');
}

/** Normaliza un contract real del backend a la forma "empresa" del módulo. */
export function empresaDeContract(contract) {
  const c = contract?.companyId || {};
  return {
    id: contract?._id,
    nombre: c.name ?? '—',
    rut: c.businessIdentifier ?? '',
    contacto: c.contacts?.primary ?? null,
    status: contract?.status,
    isBlocked: !!contract?.paymentBlock?.isBlocked,
    contract,
  };
}

/**
 * Items documentales de un contract contra la plantilla.
 * `stored` = objeto de localStorage ({ docs: { [docTipoId]: {estado, venceAt?,
 * solicitadoAt?} } }) o null.
 * - DOC_CONTRATO parte del contrato REAL (dateSigned / validityPeriod); los
 *   overrides guardados en localStorage ganan igualmente sobre esa base.
 * - Un estado guardado 'vigente' con venceAt se recalcula con el semáforo, así
 *   un doc aprobado hace meses pasa a por_vencer/vencido solo.
 */
export function itemsDeContract(contract, stored, hoy = new Date()) {
  return PLANTILLA_REGLAS.map((regla) => {
    const doc = stored?.docs?.[regla.docTipoId] ?? null;
    let estado = 'pendiente';
    let venceAt;

    if (regla.docTipoId === DOC_CONTRATO) {
      const firmado = contract?.dateSigned || contract?.validityPeriod?.startDate;
      if (firmado) {
        venceAt = contract?.validityPeriod?.endDate || undefined;
        estado = estadoDocPara(venceAt, hoy);
      }
    }

    if (doc?.estado) {
      estado = doc.estado;
      venceAt = doc.venceAt ?? venceAt;
      if (estado === 'vigente' && venceAt) {
        estado = estadoDocPara(venceAt, hoy);
      }
    }

    const bloquea = !!regla.bloqueoAlVencer && estado === 'vencido';
    return {
      regla,
      docTipoId: regla.docTipoId,
      nombre: regla.nombre,
      obligatorio: !!regla.obligatorio,
      estado,
      venceAt,
      bloquea,
      doc,
    };
  });
}

// Frase de un item para la lista de motivos (grave → leve).
function fraseDeItem(item, t = plain, hoy = new Date()) {
  const nombre = item.nombre;
  if (item.estado === 'vencido') {
    const d = item.venceAt ? Math.abs(diasHasta(item.venceAt, hoy)) : 0;
    const base = t('{{nombre}} venció hace {{d}} d', { nombre, d });
    return item.bloquea ? `${base} — ${t('requiere renovación')}` : base;
  }
  if (item.estado === 'por_vencer') {
    const d = item.venceAt ? diasHasta(item.venceAt, hoy) : 0;
    return d === 0
      ? t('{{nombre}} vence hoy', { nombre })
      : t('{{nombre}} vence en {{d}} d', { nombre, d });
  }
  if (item.estado === 'rechazado') {
    return t('{{nombre}}: se rechazó en la revisión de riesgo', { nombre });
  }
  if (item.estado === 'en_revision') {
    return t('{{nombre}} en revisión', { nombre });
  }
  return t('{{nombre}} sin cargar', { nombre });
}

/**
 * Motivos legibles del resumen (grave → leve). Recibe `t` para que lo
 * renderizado pase por i18n; sin `t` cae al español interpolado.
 */
export function motivosDe(resumen, t = plain, hoy = new Date()) {
  const { bloqueantes, vencidos, porVencer, faltantes, criticosFaltantes } = resumen.partes;
  const motivos = [];
  bloqueantes.forEach((i) => motivos.push(fraseDeItem(i, t, hoy)));
  criticosFaltantes.forEach((i) => {
    if (i.docTipoId === DOC_CONTRATO) {
      motivos.push(
        i.estado === 'en_revision'
          ? t('Contrato marco en revisión')
          : t('Sin contrato marco firmado — se opera en prepago'),
      );
    } else {
      motivos.push(fraseDeItem(i, t, hoy));
    }
  });
  vencidos.forEach((i) => motivos.push(fraseDeItem(i, t, hoy)));
  porVencer.forEach((i) => motivos.push(fraseDeItem(i, t, hoy)));
  const noCriticos = faltantes.filter((i) => !CRITICOS.includes(i.docTipoId));
  if (noCriticos.length) {
    motivos.push(
      t('Pendiente no crítico: {{nombres}}', {
        nombres: noCriticos.map((i) => i.nombre).join(', '),
      }),
    );
  }
  if (!motivos.length) {
    motivos.push(t('Carpeta completa y vigente contra la plantilla del courier'));
  }
  return motivos;
}

/**
 * Resumen de cumplimiento de un contract contra la plantilla.
 * Precedencia del estado agregado:
 * 1) algún obligatorio bloqueante vencido → 'bloqueado'
 * 2) falta un documento CRÍTICO (eval/contrato) → 'incompleto'
 * 3) hay por vencer o vencidos no bloqueantes → 'por_vencer'
 * 4) si no → 'al_dia'
 * Un faltante NO crítico jamás cambia el estado (solo baja la barra + motivo).
 * "Por vencer" SIGUE contando como conforme en la barra X/Y.
 */
export function resumenDeContract(contract, stored, hoy = new Date()) {
  const empresa = empresaDeContract(contract);
  const items = itemsDeContract(contract, stored, hoy);
  const obligItems = items.filter((i) => i.obligatorio);
  const conformes = obligItems.filter(
    (i) => i.estado === 'vigente' || i.estado === 'por_vencer',
  ).length;

  const bloqueantes = obligItems.filter((i) => i.bloquea);
  const vencidos = obligItems.filter((i) => i.estado === 'vencido' && !i.bloquea);
  const porVencer = obligItems.filter((i) => i.estado === 'por_vencer');
  const faltantes = obligItems.filter((i) =>
    ['pendiente', 'rechazado', 'en_revision'].includes(i.estado),
  );
  const criticosFaltantes = faltantes.filter((i) => CRITICOS.includes(i.docTipoId));

  let estado;
  if (bloqueantes.length > 0) estado = 'bloqueado';
  else if (criticosFaltantes.length > 0) estado = 'incompleto';
  else if (porVencer.length > 0 || vencidos.length > 0) estado = 'por_vencer';
  else estado = 'al_dia';

  const evaluacion = items.find((i) => i.docTipoId === DOC_EVAL);
  const contratoItem = items.find((i) => i.docTipoId === DOC_CONTRATO);
  const diasEval = evaluacion?.venceAt ? diasHasta(evaluacion.venceAt, hoy) : undefined;

  const resumen = {
    empresa,
    items,
    estado,
    partes: { bloqueantes, vencidos, porVencer, faltantes, criticosFaltantes },
    conformes,
    obligatorios: obligItems.length,
    evaluacion,
    contratoItem,
    diasEval,
  };
  // Motivos en español (fallback); los componentes usan motivosDe(resumen, t)
  // para la versión i18n.
  resumen.motivos = motivosDe(resumen, plain, hoy);
  return resumen;
}

/** KPIs agregados de la cartera a partir de una lista de resúmenes. */
export function resumenCartera(resumenes) {
  const conteo = (e) => resumenes.filter((r) => r.estado === e).length;
  let limiteTotal = 0;
  let utilizadoTotal = 0;
  let expuestoBloqueado = 0;
  resumenes.forEach((r) => {
    const cuenta = cuentaDe(r.empresa.id);
    limiteTotal += cuenta.limite;
    utilizadoTotal += cuenta.utilizado;
    if (r.estado === 'bloqueado') expuestoBloqueado += cuenta.utilizado;
  });
  return {
    total: resumenes.length,
    alDia: conteo('al_dia'),
    porVencer: conteo('por_vencer'),
    incompletos: conteo('incompleto'),
    bloqueados: conteo('bloqueado'),
    // Incluye evaluaciones ya vencidas (días negativos).
    evaluacionesCriticas: resumenes.filter(
      (r) => r.diasEval !== undefined && r.diasEval <= 30,
    ).length,
    limiteTotal,
    utilizadoTotal,
    expuestoBloqueado,
  };
}

/**
 * Cuenta corriente comercial de una empresa. El backend NO expone límites de
 * crédito todavía: SIEMPRE devuelve el fallback prepago (la UI que la muestre
 * lleva PendingBackendNotice).
 */
export function cuentaDe(contractId) {
  return {
    limite: 0,
    utilizado: 0,
    condicionesPago: 'Prepago / contado',
    clasificacion: 'C',
    analista: 'Sin asignar',
    ejecutivo: 'Sin asignar',
  };
}

// Mapas de presentación del estado agregado. Los literales son las claves i18n
// (envolver con t() al pintar). El mapa de iconos lucide vive en
// ComplianceShared.jsx (ICONO_ESTADO) para mantener este archivo sin React.
export const ESTADO_CUMPLIMIENTO_LABEL = {
  al_dia: 'Al día',
  por_vencer: 'Por vencer',
  incompleto: 'Documentación incompleta',
  bloqueado: 'Bloqueado por documentación',
};

export const ESTADO_CUMPLIMIENTO_CORTO = {
  al_dia: 'Al día',
  por_vencer: 'Por vencer',
  incompleto: 'Incompleta',
  bloqueado: 'Bloqueado',
};

export const ESTADO_CUMPLIMIENTO_TONE = {
  al_dia: 'success',
  por_vencer: 'warning',
  incompleto: 'info',
  bloqueado: 'error',
};
